import {
    caseInsensitiveComparator,
    comparator,
    composeComparators,
    ExposedValueWhileLoading,
    filterNulls,
    FormValue,
    fromControlWithDebounceTime,
    includesIgnoreCase,
    keyComparator,
    numericLocaleComparator,
    proBraLocComparator,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, signal, viewChildren} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {DateTime} from 'luxon';
import {
    BehaviorSubject,
    combineLatest,
    map,
    Observable,
    scan,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import {MeterService} from '../meter.service';
import {DatePipe, LoadComponent, PaginationComponent, SelectableItem} from '@brunata-angular/ui';
import {selectBuilding} from 'src/app/state/selectors';
import {Store} from '@ngrx/store';
import {applySortingToHeaders, SortableTableHeaderComponent, Sorting} from '@brunata-angular/ui';
import {InputDropdownCheckboxesComponent} from '@brunata-angular/ui';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {InputTypeaheadComponent} from '@brunata-angular/ui';
import {MeterEvent} from './event-alarm.model';
import {uniqBy} from 'lodash-es';
import {DateRange, InputDateRangePickerComponent} from '@brunata-angular/ui';
import {InputDateGroupComponent} from '@brunata-angular/ui';

type EventFilterFormGroup = FormGroup<{
    address: FormControl<string | null>;
    proBraLoc: FormControl<string | null>;
    meterSuperType: FormControl<SelectableItem[] | null>;
    alloUnitType: FormControl<SelectableItem[] | null>;
    meterNo: FormControl<string | null>;
    meterModel: FormControl<string | null>;
    severity: FormControl<SelectableItem[] | null>;
    eventType: FormControl<SelectableItem[] | null>;
    categoryName: FormControl<SelectableItem[] | null>;
    action: FormControl<string | null>;
    event: FormControl<DateTime | null>;
    createdDate: FormControl<DateTime | null>;
    mountingDate: FormControl<DateTime | null>;
    dismountedDate: FormControl<DateTime | null>;
    meterPlacement: FormControl<string | null>;
}>;

type EventFilter = FormValue<EventFilterFormGroup>;

type ColumnHeader = {
    label: string;
    column: string;
    formControl?: FormControl<string | SelectableItem[] | DateTime | boolean | null>;
    formType?: 'text' | 'selectMultiple' | 'typeAhead' | 'date';
};

@Component({
    selector: 'app-event-alarms-page',
    templateUrl: './event-alarms-page.component.html',
    styleUrl: './event-alarms-page.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        TranslatePipe,
        LoadComponent,
        ReactiveFormsModule,
        PaginationComponent,
        SortableTableHeaderComponent,
        DatePipe,
        InputTextGroupComponent,
        InputDropdownCheckboxesComponent,
        InputTypeaheadComponent,
        InputDateRangePickerComponent,
        InputDateGroupComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventAlarmsPageComponent {
    private readonly store = inject(Store);
    private readonly headers = viewChildren(SortableTableHeaderComponent);
    page = signal<number>(1);
    pageSize = signal<number>(20);
    private sorting = new BehaviorSubject<Sorting>({column: '', direction: 'asc'});

    readonly dateRangeFormControl = new FormControl<DateRange>(
        {
            startDate: DateTime.now().minus({months: 1}),
            endDate: DateTime.now(),
        },
        {nonNullable: true},
    );

    readonly filterFormGroup: EventFilterFormGroup = this.formBuilder.group({
        address: new FormControl<string | null>(null),
        proBraLoc: new FormControl<string | null>(null),
        meterSuperType: new FormControl<SelectableItem[] | null>([]),
        alloUnitType: new FormControl<SelectableItem[] | null>([]),
        meterNo: new FormControl<string | null>(null),
        meterModel: new FormControl<string | null>(null),
        severity: new FormControl<SelectableItem[] | null>([]),
        eventType: new FormControl<SelectableItem[] | null>([]),
        categoryName: new FormControl<SelectableItem[] | null>([]),
        action: new FormControl<string | null>(null),
        event: new FormControl<DateTime | null>(null),
        createdDate: new FormControl<DateTime | null>(null),
        mountingDate: new FormControl<DateTime | null>(null),
        dismountedDate: new FormControl<DateTime | null>(null),
        meterPlacement: new FormControl<string | null>(null),
    });
    readonly columnHeads: ColumnHeader[] = this.getColumnHead();
    private readonly buildingNo$: Observable<string> = this.store.select(selectBuilding).pipe(
        map(buildingState =>
            buildingState.status === 'success' ? buildingState.buildingNo : null,
        ),
        filterNulls(),
    );
    private readonly dateRange$: Observable<{
        startDate: DateTime | null;
        endDate: DateTime | null;
    }> = fromControlWithDebounceTime(this.dateRangeFormControl).pipe(
        filterNulls(),
        map(dateRange => ({
            startDate: dateRange.startDate ?? DateTime.now().minus({months: 1}),
            endDate: dateRange.endDate ?? DateTime.now(),
        })),
    );

    private readonly events$ = combineLatest({
        dateRange: this.dateRange$,
        buildingNo: this.buildingNo$,
    }).pipe(
        switchMap(({dateRange, buildingNo}) =>
            this.getEvents(buildingNo, dateRange.startDate, dateRange.endDate).pipe(
                map(measuringPoints => ({
                    loading: false,
                    value: measuringPoints,
                })),
                startWith({loading: true, value: []}),
            ),
        ),
        scan((previous, current) => ({
            loading: current.loading,
            value: current.loading ? previous.value : current.value,
        })),
        tap(meterEvents => {
            if (!meterEvents.loading) {
                this.patchFilterFormGroupValues(meterEvents.value);
            }
        }),
    );

    readonly view$: Observable<{
        meterEvents: ExposedValueWhileLoading<MeterEvent[]>;
        count: {
            critical: number;
            warning: number;
            info: number;
        };
        meterModelsMap: Map<string | null, string>;
        actionMap: Map<string | null, string>;
        buildingNo: string;
    }> = combineLatest({
        events: this.events$,
        sorting: this.sorting,
        filter: fromControlWithDebounceTime(this.filterFormGroup).pipe(
            map(() => this.filterFormGroup.getRawValue()),
        ),
        buildingNo: this.buildingNo$,
    }).pipe(
        tap(() => this.page.set(1)),
        map(({events, sorting, filter, buildingNo}) => ({
            meterEvents: {
                value: this.sortMeterEvents(this.filterMeterEvents(events.value, filter), sorting),
                loading: events.loading,
            },
            count: {
                critical: events.value.filter(event => event.severity === 'CRITICAL').length,
                warning: events.value.filter(event => event.severity === 'WARNING').length,
                info: events.value.filter(event => event.severity === 'INFO').length,
            },
            meterModelsMap: new Map(
                uniqBy(events.value, meterEvent => meterEvent.meterModel).map(meterEvent => [
                    meterEvent.meterModel,
                    meterEvent.meterModel,
                ]),
            ),
            actionMap: new Map(
                uniqBy(events.value, meterEvent => meterEvent.action)
                    .filter(event => event.action != null)
                    .map(meterEvent => [
                        this.translateService.translate('event-alarms_' + meterEvent.action),
                        this.translateService.translate('event-alarms_' + meterEvent.action),
                    ]),
            ),
            buildingNo: buildingNo,
        })),
    );

    private readonly severitySortOrder: string[] = ['CRITICAL', 'WARNING', 'INFO'];

    constructor(
        private formBuilder: FormBuilder,
        private meterService: MeterService,
        private translateService: TranslateService,
    ) {}

    private getColumnHead(): ColumnHeader[] {
        return [
            {
                label: this.translateService.translate('event-alarms_severity'),
                column: 'severity',
                formControl: this.filterFormGroup.controls.severity,
                formType: 'selectMultiple',
            },
            {
                label: this.translateService.translate('event-alarms_EventType'),
                column: 'eventType',
                formControl: this.filterFormGroup.controls.eventType,
                formType: 'selectMultiple',
            },
            {
                label: this.translateService.translate('event-alarms_Category'),
                column: 'categoryName',
                formControl: this.filterFormGroup.controls.categoryName,
                formType: 'selectMultiple',
            },
            {
                label: this.translateService.translate('event-alarms_Action'),
                column: 'action',
                formControl: this.filterFormGroup.controls.action,
                formType: 'typeAhead',
            },
            {
                label: this.translateService.translate('properties_proBraLoc', 'online', 'systems'),
                column: 'proBraLoc',
                formControl: this.filterFormGroup.controls.proBraLoc,
                formType: 'text',
            },
            {
                label: this.translateService.translate('properties_address', 'online', 'systems'),
                column: 'address',
                formControl: this.filterFormGroup.controls.address,
                formType: 'text',
            },
            {
                label: this.translateService.translate('monitoring_meterType'),
                column: 'meterSuperType',
                formControl: this.filterFormGroup.controls.meterSuperType,
                formType: 'selectMultiple',
            },
            {
                label: this.translateService.translate('properties_usage', 'online', 'systems'),
                column: 'alloUnitType',
                formControl: this.filterFormGroup.controls.alloUnitType,
                formType: 'selectMultiple',
            },
            {
                label: this.translateService.translate('properties_meterNo', 'online', 'systems'),
                column: 'meterNo',
                formControl: this.filterFormGroup.controls.meterNo,
                formType: 'text',
            },
            {
                label: this.translateService.translate(
                    'properties_meterModel',
                    'online',
                    'systems',
                ),
                column: 'meterModel',
                formControl: this.filterFormGroup.controls.meterModel,
                formType: 'typeAhead',
            },
            {
                label: this.translateService.translate(
                    'properties_mountingDate',
                    'online',
                    'common',
                ),
                column: 'mountingDate',
                formType: 'date',
                formControl: this.filterFormGroup.controls.mountingDate,
            },
            {
                label: this.translateService.translate('utilities_dismount-date'),
                column: 'dismountedDate',
                formType: 'date',
                formControl: this.filterFormGroup.controls.dismountedDate,
            },
            {
                label: this.translateService.translate('properties_placement', 'online', 'systems'),
                column: 'meterPlacement',
                formType: 'text',
                formControl: this.filterFormGroup.controls.meterPlacement,
            },
            {
                label: this.translateService.translate(
                    'manipulation_EVENTDATE',
                    'online',
                    'reportagent',
                ),
                column: 'createdDate',
                formType: 'date',
                formControl: this.filterFormGroup.controls.createdDate,
            },
        ];
    }

    private getEvents(
        buildingNo: string,
        startDate: DateTime | null,
        endDate: DateTime | null,
    ): Observable<MeterEvent[]> {
        return this.meterService.getLatestEvents(
            buildingNo,
            startDate ?? DateTime.now().minus({month: 1}),
            endDate ?? DateTime.now(),
        );
    }

    private patchFilterFormGroupValues(meterEvents: MeterEvent[]): void {
        this.filterFormGroup.patchValue(
            {
                meterSuperType: uniqBy(meterEvents, meterEvent => meterEvent.meterSuperType).map(
                    meterEvent => ({
                        name: this.translateService.translate(
                            'mappers_meterType.' + meterEvent.meterSuperType,
                            'online',
                            'common',
                        ),
                        selected: false,
                    }),
                ),
                alloUnitType: uniqBy(meterEvents, meterEvent => meterEvent.alloUnitType).map(
                    meterEvent => ({
                        name:
                            meterEvent.alloUnitType +
                            ' - ' +
                            this.translateService.translate(
                                'mappers_allocationUnitMap.' + meterEvent.alloUnitType,
                                'online',
                                'common',
                            ),
                        selected: false,
                    }),
                ),
                severity: uniqBy(meterEvents, meterEvent => meterEvent.severity)
                    .map(meterEvent => ({
                        name: meterEvent.severity,
                        selected: false,
                    }))
                    .sort((a, b) => {
                        const order = ['CRITICAL', 'WARNING', 'INFO'];
                        return order.indexOf(a.name) - order.indexOf(b.name);
                    })
                    .map(meterEvent => ({
                        name:
                            meterEvent.name === 'CRITICAL'
                                ? this.translateService.translate('utilities_TABLE_CRITICAL')
                                : meterEvent.name === 'WARNING'
                                  ? this.translateService.translate(
                                        'common_warning',
                                        'online',
                                        'common',
                                    )
                                  : this.translateService.translate('utilities_INFO'),
                        selected: false,
                    })),
                eventType: uniqBy(meterEvents, meterEvent => meterEvent.eventType).map(
                    meterEvent => ({
                        name: this.translateService.translate(
                            'event-alarms_' + meterEvent.eventType,
                        ),
                        selected: false,
                    }),
                ),
                categoryName: uniqBy(meterEvents, meterEvent => meterEvent.categoryName).map(
                    meterEvent => ({
                        name: this.translateService.translate(
                            'event-alarms_category_' + meterEvent.categoryName,
                        ),
                        selected: false,
                    }),
                ),
            },
            {emitEvent: false},
        );
    }

    onSort(sorting: Sorting): void {
        this.sorting.next(sorting);
        applySortingToHeaders(this.headers(), sorting);
    }

    private getColumnComparator(
        column: keyof MeterEvent,
        order: 'asc' | 'desc' = 'asc',
    ): (a: MeterEvent, b: MeterEvent) => number {
        switch (column) {
            case 'severity':
                return comparator({
                    selector: event => event[column],
                    compare: (a, b) =>
                        this.severitySortOrder.indexOf(a) - this.severitySortOrder.indexOf(b),
                    order: order,
                });
            case 'eventType':
            case 'action':
                return keyComparator(
                    event => this.translateService.translate('event-alarms_' + event[column]),
                    order,
                );
            case 'meterSuperType':
                return keyComparator(
                    event =>
                        this.translateService.translate(
                            'mappers_meterType.' + event.meterSuperType,
                            'online',
                            'common',
                        ),
                    order,
                );
            case 'alloUnitType':
                return keyComparator(
                    event =>
                        this.translateService.translate(
                            'mappers_allocationUnitMap.' + event.alloUnitType,
                            'online',
                            'common',
                        ),
                    order,
                );
            case 'categoryName':
                return keyComparator(
                    event =>
                        this.translateService.translate(
                            'event-alarms_category_' + event.categoryName,
                        ),
                    order,
                );
            case 'meterNo':
                return composeComparators([
                    numericLocaleComparator(event => event.meterNo, order),
                    numericLocaleComparator(event => event.printedSerialNo ?? '', order),
                ]);
            case 'proBraLoc':
                return proBraLocComparator(order);
            case 'address':
            case 'meterModel':
            case 'meterPlacement':
                return caseInsensitiveComparator(event => event[column] ?? '', order);
            case 'mountingDate':
            case 'dismountedDate':
            case 'createdDate':
                return keyComparator(event => event[column], order);
            default:
                return composeComparators([
                    keyComparator(event => event.createdDate, 'desc'),
                    comparator({
                        selector: event => event.severity,
                        compare: (a, b) =>
                            this.severitySortOrder.indexOf(a) - this.severitySortOrder.indexOf(b),
                        order: 'asc',
                    }),
                ]);
        }
    }

    private sortMeterEvents(meterEvents: MeterEvent[], sorting: Sorting): MeterEvent[] {
        const columnComparator = this.getColumnComparator(
            sorting.column as keyof MeterEvent,
            sorting.direction,
        );
        return [...meterEvents].sort(columnComparator);
    }

    private filterMeterEvents(meterEvents: MeterEvent[], filter: EventFilter): MeterEvent[] {
        return meterEvents.filter(event => {
            const createDateFilter =
                filter.createdDate != null
                    ? event.createdDate.hasSame(filter.createdDate, 'day')
                    : true;
            const mountingDateFilter =
                filter.mountingDate != null
                    ? (event.mountingDate?.hasSame(filter.mountingDate, 'day') ?? false)
                    : true;
            const dismountedDateFilter =
                filter.dismountedDate != null
                    ? (event.dismountedDate?.hasSame(filter.dismountedDate, 'day') ?? false)
                    : true;
            const proBraLocFilter = includesIgnoreCase(event.proBraLoc, filter.proBraLoc);
            const addressFilter = includesIgnoreCase(event.address, filter.address);
            const meterNoFilter = includesIgnoreCase(
                `${event.meterNo}${
                    event.printedSerialNo != null ? ` (${event.printedSerialNo})` : ''
                }`,
                filter.meterNo,
            );
            const meterModelFilter = includesIgnoreCase(event.meterModel, filter.meterModel);
            const actionFilter = includesIgnoreCase(
                this.translateService.translate('event-alarms_' + event.action),
                filter.action,
            );
            const meterSuperTypeFilter = this.filterMeterEventsFromList(
                event.meterSuperType,
                filter.meterSuperType,
                'type',
            );
            const alloUnitTypeFilter = this.filterMeterEventsFromList(
                event.alloUnitType,
                filter.alloUnitType,
                'usage',
            );
            const severityFilter = this.filterMeterEventsFromList(
                event.severity,
                filter.severity,
                'severity',
            );
            const eventTypeFilter = this.filterMeterEventsFromList(
                event.eventType,
                filter.eventType,
                'eventType',
            );
            const categoryNameFilter = this.filterMeterEventsFromList(
                event.categoryName,
                filter.categoryName,
                'categoryName',
            );
            const meterPlacement = includesIgnoreCase(event.meterPlacement, filter.meterPlacement);
            return (
                createDateFilter &&
                proBraLocFilter &&
                addressFilter &&
                meterNoFilter &&
                meterModelFilter &&
                actionFilter &&
                meterSuperTypeFilter &&
                alloUnitTypeFilter &&
                severityFilter &&
                eventTypeFilter &&
                categoryNameFilter &&
                meterPlacement &&
                dismountedDateFilter &&
                mountingDateFilter
            );
        });
    }

    private filterMeterEventsFromList(
        value: string | null,
        filterValue: SelectableItem[] | null,
        column: string,
    ): boolean {
        const valuesToFilter = filterValue
            ?.filter(selectableItem => selectableItem.selected)
            .map(selectableItem => selectableItem.name);
        if (valuesToFilter?.length === 0) {
            return true;
        }
        if (value === null) {
            return (
                filterValue?.some(
                    selectableItem => selectableItem.selected && selectableItem.name === '',
                ) ?? true
            );
        }
        switch (column) {
            case 'severity':
                return (
                    filterValue?.some(
                        selectableItem =>
                            selectableItem.selected &&
                            selectableItem.name.toLowerCase() ===
                                (value === 'CRITICAL'
                                    ? this.translateService.translate('utilities_TABLE_CRITICAL')
                                    : value === 'WARNING'
                                      ? this.translateService.translate(
                                            'common_warning',
                                            'online',
                                            'common',
                                        )
                                      : this.translateService.translate('utilities_INFO')
                                ).toLowerCase(),
                    ) ?? true
                );
            case 'category':
                return (
                    filterValue?.some(
                        selectableItem =>
                            selectableItem.selected &&
                            selectableItem.name ===
                                this.translateService.translate('event-alarms_category_' + value),
                    ) ?? true
                );
            case 'type':
                return (
                    filterValue?.some(
                        selectableItem =>
                            selectableItem.selected &&
                            selectableItem.name.toLowerCase() ===
                                this.translateService
                                    .translate('mappers_meterType.' + value, 'online', 'common')
                                    .toLowerCase(),
                    ) ?? true
                );
            case 'eventType':
                return (
                    filterValue?.some(
                        selectableItem =>
                            selectableItem.selected &&
                            selectableItem.name.toLowerCase() ===
                                this.translateService
                                    .translate('event-alarms_' + value)
                                    .toLowerCase(),
                    ) ?? true
                );
            case 'usage':
                return (
                    filterValue?.some(
                        selectableItem =>
                            selectableItem.selected &&
                            selectableItem.name.toLowerCase() ===
                                `${value} - ${this.translateService.translate('mappers_allocationUnitMap.' + value, 'online', 'common')}`.toLowerCase(),
                    ) ?? true
                );

            default:
                return (
                    filterValue?.some(
                        selectableItem =>
                            selectableItem.selected &&
                            selectableItem.name.toLowerCase() === value.toLowerCase(),
                    ) ?? true
                );
        }
    }

    downloadLatestEventExportFile(
        buildingNo: string,
        startDate: DateTime | null,
        endDate: DateTime | null,
    ): void {
        if (startDate == null || endDate == null || !startDate.isValid || !endDate.isValid) {
            return;
        }

        this.meterService.downloadLatestEventExportFile(buildingNo, startDate, endDate);
    }
}
